import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedListenableSubscribable } from '@benzinga/subscribable';
import { FormattedVideoItem, VideosResponse, YouTubeClearCacheParams } from './entities';
import { VideosRestful } from './restful';
import { Session } from '@benzinga/session';

interface ErrorEvent {
  error?: SafeError;
  errorType: 'video:get_video_error' | 'video:get_youbtube_playlist_error' | 'video:get_youbtube_livestream_error';
  type: 'error';
}

interface VideosUpdateEvent {
  type: 'video:retrieving_video';
}

interface YouTubePlaylistEvent {
  type: 'video:retrieving_youtube_playlist';
}

interface YouTubeLivestream {
  type: 'video:retrieving_youtube_livestream';
}

export type VideosRequestEvent = ErrorEvent | VideosUpdateEvent | YouTubePlaylistEvent | YouTubeLivestream;

interface VideosFunctions {
  getFeaturedVideos: VideosRequest['getFeaturedVideos'];
}

export class VideosRequest extends ExtendedListenableSubscribable<VideosRequestEvent, VideosFunctions> {
  private restful: VideosRestful;

  constructor(session: Session) {
    super();
    this.restful = new VideosRestful(session);
  }

  public getFeaturedVideos = async (): SafePromise<VideosResponse | undefined> => {
    this.dispatch({
      type: 'video:retrieving_video',
    });
    const res = await this.restful.getFeaturedVideos();
    if (res.err) {
      this.dispatch({
        error: res.err,
        errorType: 'video:get_video_error',
        type: 'error',
      });
    }
    return { ok: res.ok };
  };

  public getYouTubePlaylist = async (id: string, options): SafePromise<FormattedVideoItem[]> => {
    this.dispatch({
      type: 'video:retrieving_youtube_playlist',
    });
    const res = await this.restful.getYouTubePlaylist(id, options);
    if (res.err) {
      //console.error('Error retrieving youtube playlist', res.err);
      this.dispatch({
        error: res.err,
        errorType: 'video:get_youbtube_playlist_error',
        type: 'error',
      });
    }
    return { ok: res.ok || [] };
  };

  public resetYouTubeCache = async ({ action, keys, pattern }: YouTubeClearCacheParams) => {
    const res = await this.restful.resetYouTubeCache({ action, keys, pattern });
    return res;
  };

  protected onSubscribe(): VideosFunctions {
    return {
      getFeaturedVideos: this.getFeaturedVideos,
    };
  }
}
